import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { msalInstance, apiScopes, API_BASE } from "../../auth/msalConfig";
import { api } from "../../api/client";

const TEMPLATES = [
  { id: "manager_submit", title: "Manager — new claim to review", desc: "Sent to a claimant's manager when they submit a claim." },
  { id: "manager_approved_payroll", title: "Payroll — manager-approved", desc: "Sent to the payroll team when the manager approves and the amount needs payroll review." },
  { id: "manager_approved_no_payroll", title: "Claimant — approved (no payroll)", desc: "Sent to claimant if their claim is below the payroll-skip threshold." },
  { id: "payroll_approved", title: "Claimant — approved for payment", desc: "Sent to claimant when payroll signs off the claim." },
  { id: "rejected_manager", title: "Claimant — rejected by manager", desc: "Sent to claimant when their manager rejects the claim." },
  { id: "rejected_payroll", title: "Claimant — rejected by payroll", desc: "Sent to claimant when payroll rejects the claim." },
  { id: "paid", title: "Claimant — paid", desc: "Sent when payroll marks the claim as paid." },
  { id: "recall", title: "Claimant — recalled", desc: "Sent to claimant when they recall a submitted claim." },
];

export default function EmailPreviewsAdmin() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recipient = to.trim() || me?.email || "";

  const sendOne = async (templateId: string | null) => {
    setBusy(templateId ?? "all");
    setError(null);
    setResult(null);
    try {
      const account = msalInstance.getAllAccounts()[0];
      const tok = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
      const body: any = { to: recipient };
      if (templateId) body.only = [templateId];
      const res = await fetch(`${API_BASE}/api/manage/email-previews`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok.accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.detail ?? j?.title ?? `HTTP ${res.status}`);
      }
      const j = await res.json();
      setResult(`Sent ${j.templates.length} ${j.templates.length === 1 ? "email" : "emails"} to ${j.sent_to}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Email previews</h1>
        <p className="text-slate-600 mt-1">Send any of the live email templates to your inbox so you can review the styling and copy.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Send to</label>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={me?.email ?? "you@liaise.com"}
            className="w-full max-w-md border border-slate-300 rounded-lg px-3 py-2.5 focus:border-liaise-navy focus:ring-1 focus:ring-liaise-navy outline-none"
          />
          <p className="text-xs text-slate-500 mt-1">Defaults to your signed-in email.</p>
        </div>

        <button
          onClick={() => sendOne(null)}
          disabled={busy !== null}
          className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
        >
          {busy === "all" ? "Sending…" : "Send all 8 to me"}
        </button>

        {result && <div className="text-emerald-700 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{result}</div>}
        {error && <div className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2 font-mono">{error}</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {TEMPLATES.map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="font-medium text-liaise-navy">{t.title}</div>
            <div className="text-slate-600 text-sm mt-1">{t.desc}</div>
            <button
              onClick={() => sendOne(t.id)}
              disabled={busy !== null}
              className="mt-3 text-liaise-navy text-sm font-medium hover:underline disabled:opacity-50"
            >
              {busy === t.id ? "Sending…" : "Send to me →"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
